import { Component, Injector, Input, OnChanges, OnDestroy, OnInit, SimpleChanges, ViewEncapsulation, ChangeDetectionStrategy } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatSelectChange } from '@angular/material/select';
import { takeUntil } from 'rxjs';
import { IntegrationTypeEnum } from '../../../../public-api';
import { AbstractComponent } from '../../../commons/abstract.component';
import { Channel } from '../../../models/channel.model';
import { Integration } from '../../../models/integration.model';
import { SettingsModule } from '../../../models/settings-module.model';
import { IntegrationService } from '../../../services/integration.service';
import { SettingsModuleService } from '../../../services/settings-module.service';
import { CSATModuleEditComponent } from './csat-module-edit/csat-module-edit.component';
import { CustomerPortfolioModuleEditComponent } from './customer-portfolio-module-edit/customer-portfolio-module-edit.component';
import { IAScheduleModalComponent } from './ia-schedule-modal/ia-schedule-modal.component';

@Component({
    selector: 'settings-module-component',
    // templateUrl: './settings-module.component.html',
    templateUrl: './settings-module.v2.component.html',
    styleUrls: ['settings-module.component.scss'],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class SettingsModuleComponent extends AbstractComponent implements OnInit, OnChanges, OnDestroy {

  @Input() channel!: Channel;

  integrations: Integration[];
  settingsModule: SettingsModule = {} as SettingsModule;

  constructor(
    injector: Injector,
    private integrationService: IntegrationService,
    private settingsModuleService: SettingsModuleService,
    public dialog: MatDialog,
  ) {
    super(injector);
  }

  ngOnInit(): void {
    this.createForm();
    this.getIntegrations();
    this.settingsModule = {} as SettingsModule;
    this.formGroup.valueChanges.subscribe(value => {
      Object.assign(this.settingsModule, value);
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!!changes['channel']?.currentValue) {
      this.settingsModule = {} as SettingsModule;
      this.channel = changes['channel']?.currentValue;
      this.getSettingsModule();
    }
  }

  override ngOnDestroy(): void {
  }

  private createForm(): void {
    this.formGroup = this.formBuilder.group({
      activeCSATModule: [false],
      activeIAModule: [false],
      activeWavoipModule: [false],
      assistant: [undefined],
      integration: [undefined],
      activeCustomerPortfolioModule: [false],
      noResponseTimeoutCustomerPortfolio: [null],
      noResponseTimeoutCSAT: [null],
      activeCopilotModule: [false],
      copilotTraining: [null],
      copilotPlaybook: [null],
    });
    if (!this.getModuleCSAT()) {
      this.formGroup.get('activeCSATModule').setValue(false);
      this.formGroup.get('activeCSATModule')?.disable();
    }
    if (!this.getModuleOpenAI() && !this.getModuleTrendGPT()) {
      this.formGroup.get('activeIAModule').setValue(false);
      this.formGroup.get('activeIAModule')?.disable();
    }
    if (!this.getModuleTrendVoip()) {
      this.formGroup.get('activeWavoipModule').setValue(false);
      this.formGroup.get('activeWavoipModule')?.disable();
    }
    if (!this.getModuleCustomerPortfolio()) {
      this.formGroup.get('activeCustomerPortfolioModule').setValue(false);
      this.formGroup.get('activeCustomerPortfolioModule')?.disable();
    }
    if (!this.isAdmin()) {
      this.formGroup.disable();
    }
    this.formGroup.valueChanges.subscribe(value => {
      Object.assign(this.settingsModule, value);
    });
  }

  getSettingsModule() {
    this.settingsModuleService.getByChannel(this.channel._id).subscribe({
      next: (settingsModule: SettingsModule) => {
        if (settingsModule) {
          this.settingsModule = settingsModule;
          this.formGroup.patchValue(this.settingsModule);
        } else {
          this.formGroup.get('activeCSATModule').setValue(false);
          this.formGroup.get('activeIAModule').setValue(false);
          this.formGroup.get('activeWavoipModule').setValue(false);
          this.formGroup.get('activeCustomerPortfolioModule').setValue(false);
          this.settingsModule.channel = this.channel;
        }
      },
      error: (err) => this.alertService.error(err.error.message),
    });
  }

  isCSATModuleActive() {
    return this.getModuleCSAT() && this.formGroup.get('activeCSATModule').value;
  }

  isIAModuleActive() {
    return (this.getModuleOpenAI() || this.getModuleTrendGPT()) && this.formGroup.get('activeIAModule').value;
  }

  isWAVoipModuleActive() {
    return this.getModuleTrendVoip() && this.formGroup.get('activeWavoipModule').value;
  }

  isCustomerPortfolioModuleActive() {
    return this.getModuleCustomerPortfolio() && this.formGroup.get('activeCustomerPortfolioModule').value;
  }

  editCSAT() {
    if (this.isAdmin()) {
      const dialogRef = this.dialog.open(CSATModuleEditComponent, {
        width: '600px',
        data: this.settingsModule
      });

      dialogRef.afterClosed().subscribe(result => {
        this.getSettingsModule();
      });
    }
  }

  editCustomerPortfolio() {
    if (this.isAdmin()) {
      const dialogRef = this.dialog.open(CustomerPortfolioModuleEditComponent, {
        width: '600px',
        data: this.settingsModule
      });

      dialogRef.afterClosed().subscribe(result => {
        this.getSettingsModule();
      });
    }
  }

  onConfirmClick() {
    const activeIAModule = this.formGroup.get('activeIAModule').value;
    const assistantValue = this.formGroup.get('assistant').value;
    if (activeIAModule && (assistantValue === null || assistantValue === '')) {
      this.alertService.warning(this.t('shared.settingsModule.selectAssistantWarning'));
      return;
    }

    this.settingsModuleService.update(this.settingsModule._id, this.settingsModule)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (settingsModule: SettingsModule) => {
          this.settingsModule = settingsModule;
          this.formGroup.patchValue(this.settingsModule);
          this.alertService.success(this.t('shared.settingsModule.updateSuccess'));
        },
        error: (err) => {
          this.alertService.error(err.error.message);
        },
      });
  }

  getIntegrations(): void {
    this.integrationService.getAssistantsByChannel(this.channel._id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (integrations: Integration[]) => {
          const filteredIntegrations: Array<Integration> = new Array<Integration>();
          if (this.getModuleOpenAI()) {
            filteredIntegrations.push(...integrations.filter(integration => integration.type === IntegrationTypeEnum.OPENAI));
          }
          if (this.getModuleTrendGPT()) {
            filteredIntegrations.push(...integrations.filter(integration => integration.type === IntegrationTypeEnum.GPT_MAKER));
          }
          this.integrations = filteredIntegrations;
        },
        error: (err) => this.alertService.error(err.error.message)
      });
  }

  getAssistantsAndAgents(integration: Integration): any[] {
    const assistants = integration?.metadata?.['assistants'] || [];
    const agents = integration?.metadata?.['agents'] || [];
    const currentChannelId = this.channel?._id;

    const filteredAssistants = assistants.filter((assistant: any) => {
      return assistant.channels?.some((ch: any) => ch._id === currentChannelId);
    });

    const filteredAgents = agents.filter((agent: any) => {
      return agent.channels?.some((ch: any) => ch._id === currentChannelId);
    });

    return [...filteredAssistants, ...filteredAgents];
  }

  findIdIntegrationByAssistantId(assistantId: string): string | null {
    for (const integration of this.integrations) {
      const assistants = this.getAssistantsAndAgents(integration);
      const foundAssistant = assistants.find((assistant: any) => assistant.id === assistantId);
      if (foundAssistant) {
        return integration._id;
      }
    }
    return null;
  }

  onSelectAssistant(selectedAssistantId: MatSelectChange): void {
    this.formGroup.get('integration').setValue(this.findIdIntegrationByAssistantId(selectedAssistantId.value));
  }

  onToggleCustomerPortfolioModuleChange(): void {
    if (this.isCustomerPortfolioModuleActive()) {
      if (!this.formGroup.get('noResponseTimeoutCustomerPortfolio').value) {
        this.formGroup.get('noResponseTimeoutCustomerPortfolio').setValue(5);
      }
    }
  }

  onToggleCSATModuleChange(): void {
    if (this.isCSATModuleActive()) {
      if (!this.formGroup.get('noResponseTimeoutCSAT').value) {
        this.formGroup.get('noResponseTimeoutCSAT').setValue(5);
      }
    }
  }

  openIAScheduleModal(): void {
    if (this.isAdmin()) {
      const dialogRef = this.dialog.open(IAScheduleModalComponent, {
        width: '1200px',
        data: this.settingsModule
      });

      dialogRef.afterClosed().subscribe(result => {
        if (result) {
          this.getSettingsModule();
        }
      });
    }
  }

  hasIAScheduleConfigured(): boolean {
    if (!this.settingsModule?.inactivityAISchedules) {
      return false;
    }
    const schedules = this.settingsModule.inactivityAISchedules;
    return !!(schedules.monday?.actived ||
      schedules.tuesday?.actived ||
      schedules.wednesday?.actived ||
      schedules.thursday?.actived ||
      schedules.friday?.actived ||
      schedules.saturday?.actived ||
      schedules.sunday?.actived);
  }

}
